import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, ShieldCheck, Check, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg, useApi, streamPost, fmtD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Card, Pill, Empty } from "@/components/kit";

const PROCESSES = ["Receipt", "Chilled storage", "Frozen storage", "Defrosting", "Preparation", "Cooking", "Cooling", "Reheating", "Hot holding", "Sous vide", "Vacuum packing", "Takeaway / delivery"];

export const Md = ({ children }) => <div className="ka-prose text-sm"><ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown></div>;

function Generator({ onSaved }) {
  const { venueId, venue } = useAuth();
  const [f, setF] = useState({ venue_type: venue?.venue_type || "restaurant", menu: "", processes: ["Receipt", "Chilled storage", "Preparation", "Cooking", "Hot holding"], notes: "" });
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(`HACCP plan – ${venue?.name || ""}`);

  const gen = async (e) => {
    e.preventDefault();
    setOut("");
    setBusy(true);
    try {
      await streamPost("/ai/haccp", f, (t) => setOut((o) => o + t));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    try {
      await api.post("/haccp", { title, venue_id: venueId, content: out });
      toast.success("HACCP plan saved as v1");
      setOut("");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggle = (p) => setF({ ...f, processes: f.processes.includes(p) ? f.processes.filter((x) => x !== p) : [...f.processes, p] });

  return (
    <Card className="mb-8">
      <div className="mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-600" /><h3 className="text-xl font-bold">AI HACCP plan generator</h3></div>
      <form onSubmit={gen} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type of business"><input data-testid="haccp-venue-type-input" className="ka-input" value={f.venue_type} onChange={(e) => setF({ ...f, venue_type: e.target.value })} /></Field>
          <Field label="Extra notes"><input data-testid="haccp-notes-input" className="ka-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g. we serve rare burgers" /></Field>
        </div>
        <Field label="Menu / main dishes *"><textarea data-testid="haccp-menu-input" required rows={3} className="ka-input" value={f.menu} onChange={(e) => setF({ ...f, menu: e.target.value })} placeholder="Paste your menu or list your main dishes" /></Field>
        <div className="flex flex-wrap gap-2">{PROCESSES.map((p) => <button key={p} type="button" onClick={() => toggle(p)} className={`ka-press rounded-full border px-3 py-1.5 text-xs font-semibold ${f.processes.includes(p) ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white text-slate-600"}`}>{p}</button>)}</div>
        <button data-testid="generate-haccp-ai-button" disabled={busy} className="ka-press inline-flex items-center gap-2 rounded-full bg-slate-950 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"><Sparkles className="h-4 w-4" />{busy ? "Writing your plan…" : "Generate HACCP plan"}</button>
      </form>
      {out && (
        <div className="mt-6 border-t border-slate-200 pt-6">
          <div data-testid="haccp-ai-output" className="max-h-[60vh] overflow-y-auto rounded-xl bg-slate-50 p-5"><Md>{out}</Md></div>
          {!busy && (
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <Field label="Plan title" className="min-w-[260px] flex-1"><input data-testid="haccp-title-input" className="ka-input" value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
              <button data-testid="save-haccp-button" onClick={save} className="ka-press h-[42px] rounded-full bg-emerald-600 px-6 text-sm font-semibold text-white hover:bg-emerald-700">Save plan</button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function PlanView({ plan, onChanged }) {
  const { user, can, venueId } = useAuth();
  const [edit, setEdit] = useState(null);
  const acked = plan.acknowledgements.some((a) => a.user_id === user.id);
  const ack = async () => {
    await api.post(`/haccp/${plan.id}/ack`);
    toast.success("Acknowledged");
    onChanged();
  };
  const saveVersion = async () => {
    try {
      await api.post("/haccp", { title: plan.title, venue_id: plan.venue_id || venueId, content: edit, parent_id: plan.id });
      toast.success(`Saved v${plan.version + 1}`);
      onChanged(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-6 space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600"><Pill status={plan.status === "active" ? "ok" : "low"} label={`v${plan.version} · ${plan.status}`} /> Review due {fmtD(plan.review_due)} · {plan.acknowledgements.length} staff acknowledged</div>
      <div className="flex flex-wrap gap-2">
        {plan.status === "active" && <button data-testid="ack-haccp-button" disabled={acked} onClick={ack} className="ka-press inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white disabled:bg-emerald-100 disabled:text-emerald-800"><Check className="h-3.5 w-3.5" />{acked ? "You've acknowledged this" : "I've read & understood"}</button>}
        {plan.status === "active" && can("manage") && edit === null && <button data-testid="new-version-haccp-button" onClick={() => setEdit(plan.content)} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold"><FilePlus2 className="h-3.5 w-3.5" />Edit as new version</button>}
      </div>
      {edit !== null ? (
        <div className="space-y-3"><textarea data-testid="haccp-edit-textarea" rows={20} className="ka-input font-mono text-xs" value={edit} onChange={(e) => setEdit(e.target.value)} /><button data-testid="save-haccp-version-button" onClick={saveVersion} className="ka-press rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white">Save new version</button></div>
      ) : <Md>{plan.content}</Md>}
    </div>
  );
}

export default function Haccp() {
  const { can } = useAuth();
  const { data, reload } = useApi("/haccp");
  const [sel, setSel] = useState(null);
  return (
    <div>
      <PageHeader eyebrow="Food safety procedures" title="HACCP plans" subtitle="Venue-specific hazard controls, versioned with review dates and staff acknowledgements." />
      {can("manage") && <Generator onSaved={reload} />}
      {data && !data.length && <Empty testid="haccp-empty" icon={ShieldCheck} title="No HACCP plans yet" text="Generate one above or ask a manager to set it up." />}
      <div className="grid gap-3 md:grid-cols-2">
        {(data || []).map((p) => (
          <button key={p.id} data-testid={`haccp-plan-${p.id}`} onClick={() => setSel(p)} className="ka-press ka-card p-5 text-left hover:border-slate-400">
            <div className="flex items-center justify-between gap-2"><span className="font-display text-lg font-bold">{p.title}</span><Pill status={p.status === "active" ? "ok" : "low"} label={p.status === "active" ? `v${p.version}` : `v${p.version} · Superseded`} /></div>
            <div className="mt-1 text-xs text-slate-500">{p.created_by_name} · {fmtD(p.created_at)} · {p.acknowledgements.length} acknowledged</div>
          </button>
        ))}
      </div>
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader><SheetTitle className="font-display text-2xl">{sel?.title}</SheetTitle></SheetHeader>
          {sel && <PlanView plan={sel} onChanged={async (close) => { const { data: list } = await api.get("/haccp"); reload(); setSel(close ? null : list.find((x) => x.id === sel.id)); }} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
